import { useMatch, useRouterState } from '@tanstack/react-router';
import type { FC } from 'react';
import {
  type AIContext,
  type SidePanelMode,
  useRouteSidePanelStore,
} from '@/shared/stores/side-panel-store';
import { AssessmentComparisonSidePanel } from '@/widgets/assessment/assessment-comparison/AssessmentComparisonSidePanel';
import { AccountingTypesSidePanel } from '@/widgets/master-management/ui/accounting-types-create-side-panel/AccountingTypesCreateSidePanel';
import { ExpenditureItemsSidePanel } from '@/widgets/master-management/ui/expenditure-items-side-panel/ExpenditureItemsSidePanel';
import { OrganizationsSidePanel } from '@/widgets/master-management/ui/organizations-create-side-panel/OrganizationsSidePanel';
import { ExpenditureEditSidePanel } from '@/widgets/settlement-statistics-converter/ui/expenditure-edit-side-panel/ExpenditureEditSidePanel';
import { MasterCreateSidePanel } from '@/widgets/settlement-statistics-converter/ui/master-create-side-panel/MasterCreateSidePanel';
import { RevenueAppropriationExpenditureSidePanel } from '@/widgets/settlement-statistics-converter/ui/revenue-appropriation-expenditure-side-panel/RevenueAppropriationExpenditureSidePanel';
import { RevenueEditSidePanel } from '@/widgets/settlement-statistics-converter/ui/revenue-edit-side-panel/RevenueEditSidePanel';
import type { RouteSidebarEffectParams } from '../lib/types';
import { useRouteSidebarEffect } from '../lib/usePageRouteEffect';
import { AIChatContents } from './ai-chat/AIChatContents';

export const RightSideMenuContents: FC = () => {
  const rightMode = useRouteSidePanelStore((state) => state.mode);
  const isRightOpen = useRouteSidePanelStore((state) => state.isOpen);
  const setOpen = useRouteSidePanelStore((state) => state.setOpen);

  // modeからcontextを取得
  const aiContextMenu = rightMode?.type === 'ai-chat' ? rightMode.context : undefined;

  const setAiContextMenu = (context: AIContext | undefined) => {
    const newMode: SidePanelMode = context ? { type: 'ai-chat', context } : { type: 'ai-chat' };
    setOpen({ isOpen: isRightOpen, mode: newMode });
  };
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });

  // useMatch で型安全にルートマッチを確認
  const isProjectCreatePage = useMatch({
    from: '/_protected/projects/create',
    shouldThrow: false,
  });
  const isProjectEditPage = useMatch({
    from: '/_protected/projects/$projectId/edit',
    shouldThrow: false,
  });
  const isProjectDetailPage = useMatch({
    from: '/_protected/projects/$projectId/',
    shouldThrow: false,
  });
  const isProjectMemoPage = useMatch({
    from: '/_protected/projects/$projectId/memo',
    shouldThrow: false,
  });

  //////////////////////////////////////////////////////////////
  // ルート変更によるEffect
  // 編集ページは詳細ページにもマッチするため、先に判定する

  const routeParams: RouteSidebarEffectParams = isProjectCreatePage
    ? {
        route: 'projectCreate',
        params: {
          fromPdf: isProjectCreatePage.search.fromPdf ?? false,
        },
      }
    : isProjectEditPage
      ? {
          route: 'projectEdit',
          params: {},
        }
      : isProjectDetailPage
        ? {
            route: 'projectDetail',
            params: {
              threadId: isProjectDetailPage.search.threadId,
            },
          }
        : isProjectMemoPage
          ? {
              // メモページでも事業詳細と同じ AI チャットメニューを共有する
              route: 'projectDetail',
              params: {
                threadId: isProjectMemoPage.search.threadId,
              },
            }
          : {
              route: undefined,
              params: undefined,
            };

  useRouteSidebarEffect({ pathname, setAiContextMenu, ...routeParams });
  //////////////////////////////////////////////////////////////

  if (rightMode?.type === 'ai-chat') {
    return <AIChatContents aiContextMenu={aiContextMenu} setAiContextMenu={setAiContextMenu} />;
  }

  if (rightMode?.type === 'settlement-statistics-converter.revenue-edit') {
    return <RevenueEditSidePanel />;
  }

  if (rightMode?.type === 'settlement-statistics-converter.expenditure-edit') {
    return <ExpenditureEditSidePanel />;
  }

  if (rightMode?.type === 'settlement-statistics-converter.master') {
    return <MasterCreateSidePanel />;
  }
  if (rightMode?.type === 'master-management.accounting-types') {
    return <AccountingTypesSidePanel />;
  }
  if (rightMode?.type === 'master-management.expenditure-items') {
    return <ExpenditureItemsSidePanel />;
  }
  if (rightMode?.type === 'master-management.organizations') {
    return <OrganizationsSidePanel />;
  }

  // 決算統計コンバーター財源充当画面で、歳出の作成・編集を行う
  if (rightMode?.type === 'settlement-statistics-converter.revenue-appropriation') {
    return <RevenueAppropriationExpenditureSidePanel />;
  }

  return <AssessmentComparisonSidePanel />;
};
